import React from 'react'
import PropTypes from 'prop-types'
import ParcelDetailsField from './ParcelDetailsField'
import ParcelDetailsSubsection from './ParcelDetailsSubsection'
import { useSelector } from 'react-redux'
import { shouldRenderOrOpen } from 'common/util.js'

/**
 * Renders a collapsible section of parcel details.
 *
 * @component
 * @param {Object} props - component props
 * @param {string} props.category - category of the parcel details
 * @param {Object} props.parcel - full parcel object
 * @param {Array} props.fields - fields to render in the section
 * @param {string} props.id - unique identifier for the section
 * @param {boolean} props.isOpen - whether the section is open
 * @param {Function} props.onToggle - fn to handle user prefs and caret animation
 * @returns {JSX.Element} Rendered component
 */


const ParcelDetailsSection = ({
  category,
  parcel,
  fields,
  id,
  isOpen,
  onToggle,
  renderHighlightButton
}) => {
  const preferences = useSelector(s => s.userProperties.parcelPrefs)

    // preferences not loaded yet so wait
    if(preferences === null) {
      return null //
    }
    const hasSomeFieldsChecked = shouldRenderOrOpen(fields, parcel, preferences)
    const resolvedIsOpen =
      isOpen === false ? false :
      isOpen === true  ? hasSomeFieldsChecked : hasSomeFieldsChecked
      // band aid fix bc additional items has 2 disclaimers
    const isNormalCategory = category !== 'Additional Items'
    return (
      <div className="panel panel-default margin-btm-md">
        <div
          className={`panel-heading${!resolvedIsOpen ? ' collapsed' : ''}`}
          role="tab"
          id={`heading-${id}`}
          data-toggle="collapse"
          href={`#collapse-${id}`}
          aria-expanded={resolvedIsOpen}
          aria-controls={`collapse-${id}`}
          onClick={() => onToggle(id)}
        >
          <h4 className="panel-title">
            <button className="no-btn width-100">
              <span className="pull-left">{category}</span>
              <i
                id={`caret-${id}`}
                className={`fa fa-angle-up pull-right`}
              />
            </button>
          </h4>
        </div>
        <div
          id={`collapse-${id}`}
          className={`panel-collapse collapse${resolvedIsOpen ? ' in' : ''}`}
          role="tabpanel"
          aria-labelledby={`heading-${id}`}
        >
      <div className="panel-body">
        {(!hasSomeFieldsChecked && isNormalCategory) ? (
          <ParcelDetailsField
            key="empty"
            field="all_fields_unchecked"
            parcel={parcel}
          />
        ) : (
          fields.map((sectionField, index) => {
            const coordinatesFields = ['coordinates', 'lat', 'lon']
            const fieldKey = Array.isArray(sectionField) ? sectionField[0] : sectionField
            const value = preferences[fieldKey]
            if(
              value === false ||
              (coordinatesFields.includes(fieldKey) && preferences.coordinates === false)
            ) {

              return null
            }
            let fieldSecondary = null
            if(
              typeof sectionField !== 'string' &&
              !(sectionField instanceof Array)
            ) {
              return (
                <ParcelDetailsSubsection
                  subhead={sectionField.subhead}
                  secFields={sectionField.fields}
                  parcel={parcel}
                  key={index}
                />
              )
            } else {
              if(Array.isArray(sectionField)) {
                fieldSecondary = sectionField[1]
                sectionField = sectionField[0]
              }
              return (
                <ParcelDetailsField
                  field={sectionField}
                  fieldSecondary={fieldSecondary}
                  parcel={parcel}
                  key={index}
                  renderHighlightButton={renderHighlightButton}
                />
              )
            }
          })
        )}
          </div>
        </div>
      </div>
    )
  }

ParcelDetailsSection.propTypes = {
  category:              PropTypes.string,
  parcel:                PropTypes.object,
  fields:                PropTypes.array,
  id:                    PropTypes.string,
  isOpen:                PropTypes.bool,
  onToggle:              PropTypes.func,
  renderHighlightButton: PropTypes.func,
  preferences:           PropTypes.object,
}

export default ParcelDetailsSection
